import { useEffect } from 'react';
import { Link } from 'react-router';
import { fhir } from '@curentia/shared';
import { formatDate } from '../lib/health';
import { useLoad } from '../lib/useLoad';
import { ConnectionStatus, ErrorText, LockButton, Page, formatMobile } from '../components';
import { DOORS, doorOf } from '../lib/doors';
import { useAction, useDevice, useSnapshot } from '../state';
import { PatientRequests, PatientShortcuts, RecentPrescriptions } from './MyHealth';
import { PracticesCard } from './Practices';

const NEXT_STEPS: Record<string, string> = {
  patient: 'Prescriptions and readings from your doctors arrive on this phone.',
  doctor: 'Open a practice to see its patients and write prescriptions.',
  pharmacist: 'Open your pharmacy to see the prescriptions sent to it.',
  staff: 'Open your practice to register patients or manage its team.',
};

function Reminders() {
  const { backupDue, settings, notice } = useSnapshot();
  return (
    <>
      {notice ? <p className="card warning">{notice}</p> : null}
      {!settings.recoveryCodeConfirmed ? (
        <section className="card warning">
          <h2>Make a recovery code</h2>
          <p>Without one, a forgotten password means losing the records on this device.</p>
          <Link to="/security" className="button small">
            Make a recovery code
          </Link>
        </section>
      ) : null}
      {backupDue ? (
        <section className="card warning" aria-label="Backup reminder">
          <h2>Back up your records</h2>
          <p>Your records live on this device. A backup file lets you restore them on a new phone.</p>
          <Link to="/security" className="button small">
            Back up now
          </Link>
        </section>
      ) : null}
    </>
  );
}

/** On a doctor's home screen: the newest notices from pharmacies about the doctor's prescriptions. */
function DispensingNotices() {
  const device = useDevice();
  const { recordsVersion } = useSnapshot();
  const { data } = useLoad(async () => (await device.doctorNotices().list()).slice(0, 5), [device, recordsVersion]);
  if (!data || data.length === 0) return null;
  return (
    <section className="card" aria-labelledby="notices-heading">
      <h2 id="notices-heading">From pharmacies</h2>
      <ul className="list" aria-label="Dispensing notices">
        {data.map((notice) => {
          let handed: string[] = [];
          let missing = 0;
          try {
            const view = fhir.readDispenseRecord(notice.dispense.resource as unknown as fhir.Bundle);
            handed = view.items.filter((i) => i.status === 'completed').map((i) => i.medicine);
            missing = view.items.length - handed.length;
          } catch {
            handed = [];
          }
          return (
            <li key={notice.key} className="member">
              <strong>
                {notice.patientName} · {notice.pharmacy.name}
              </strong>
              <span>{handed.length > 0 ? `Handed over: ${handed.join(', ')}` : 'Nothing handed over'}{missing > 0 ? `. ${missing} not dispensed.` : '.'}</span>
              <span className="muted">
                Prescribed {formatDate(notice.prescribedAt)} · told {formatDate(notice.receivedAt)}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function Home() {
  const { account, waiting } = useSnapshot();
  if (!account) return null;
  const door = doorOf(account.type);
  return (
    <Page actions={<LockButton />}>
      <h1>Hello, {account.name}</h1>
      <p className="muted">
        {door ? DOORS[door].label : 'Account'} · {formatMobile(account.mobile)}
      </p>
      <ConnectionStatus />
      <Reminders />
      <section className="card tinted">
        <h2>Waiting for you</h2>
        <p data-testid="waiting">{waiting === 0 ? 'Nothing new.' : `${waiting} new ${waiting === 1 ? 'item' : 'items'} arrived for this device.`}</p>
        <p className="muted">{NEXT_STEPS[account.type] ?? ''}</p>
      </section>
      {account.type === 'patient' ? (
        <>
          <PatientRequests />
          <RecentPrescriptions />
          <PatientShortcuts />
        </>
      ) : (
        <>
          {account.type === 'doctor' ? <DispensingNotices /> : null}
          <PracticesCard />
        </>
      )}
      <div className="links">
        {account.type === 'doctor' ? <Link to="/pharmacies">Linked pharmacies</Link> : null}
        <Link to="/security">Backup and security</Link>
      </div>
    </Page>
  );
}

export function Pending() {
  const device = useDevice();
  const { account } = useSnapshot();
  const { busy, error, run } = useAction();
  useEffect(() => {
    const timer = setInterval(() => void device.refresh().catch(() => undefined), 60_000);
    return () => clearInterval(timer);
  }, [device]);
  if (!account) return null;
  return (
    <Page actions={<LockButton />}>
      <h1>Waiting for verification</h1>
      <p>Thank you, {account.name}. Our verification team is checking your registration. You can use Curentia once they approve it.</p>
      {account.registration ? (
        <section className="card">
          <dl>
            <dt>Council</dt>
            <dd>{account.registration.council}</dd>
            <dt>Number</dt>
            <dd>{account.registration.number}</dd>
          </dl>
        </section>
      ) : null}
      <ConnectionStatus />
      <Reminders />
      <ErrorText error={error} />
      <div className="stack">
        <button type="button" className="button" disabled={busy} onClick={() => void run(() => device.refresh())}>
          {busy ? 'Checking…' : 'Check again'}
        </button>
        <Link to="/security" className="button secondary">
          Backup and security
        </Link>
      </div>
    </Page>
  );
}

export function Blocked() {
  const device = useDevice();
  const { account } = useSnapshot();
  const { error, run } = useAction();
  if (!account) return null;
  const rejected = account.status === 'rejected';
  return (
    <Page actions={<LockButton />}>
      <h1>{rejected ? 'Registration not approved' : 'Account suspended'}</h1>
      <section className="card danger">
        <p>{rejected ? 'Our verification team could not approve your registration.' : 'This account has been suspended.'}</p>
        {account.statusReason ? <p>Reason: {account.statusReason}</p> : null}
      </section>
      <p className="muted">Contact Curentia support if you think this is a mistake. Your records stay on this device.</p>
      <ErrorText error={error} />
      <button type="button" className="button secondary" onClick={() => void run(() => device.refresh())}>
        Check again
      </button>
    </Page>
  );
}
